import React from 'react';
import styled from '@emotion/styled';
import * as Yup from 'yup';
import { Formik } from 'formik';

import { Button as MuiButton, Paper as MuiPaper, TextField as MuiTextField, TextFieldProps, Typography, TypographyProps } from '@mui/material';
import { spacing } from '@mui/system';
import PageHeader from '../../components/common/PageHeader';

import { useSessionStore, useUserMessageStore } from '../../stores';
import { confirmPassword, password } from '../../validators/passwordValidators';
import { userMessages } from '../../utils/userMessages';
import { changePassword } from '../../apis/authApi';

const Paper = styled(MuiPaper)(spacing);

type ProfileTextFieldProps = Omit<TextFieldProps, 'variant'>;

const TextField: React.FC<ProfileTextFieldProps> = (props: ProfileTextFieldProps) => {
  return <MuiTextField variant="outlined" fullWidth sx={{ my: 3 }} {...props}></MuiTextField>;
};

const MainHeading: React.FC<TypographyProps> = (props: TypographyProps) => {
  return (
    <Typography
      pl={1}
      pt={6}
      pb={0}
      {...props}
      sx={{
        typography: { xs: 'h5', sm: 'h3' },
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    ></Typography>
  );
};

const Heading: React.FC<TypographyProps> = (props: TypographyProps) => {
  return (
    <Typography
      mb={2}
      ml={2}
      {...props}
      sx={{
        typography: { xs: 'h6', sm: 'h4' },
      }}
    ></Typography>
  );
};

const Button = styled(MuiButton)(spacing);

const ChangePassword: React.FC = () => {
  const setUserMessage = useUserMessageStore.use.setUserMessage();

  return (
    <Paper mt={6} p={5} sx={{ border: '1px solid', borderColor: (theme) => theme.palette.divider }}>
      <Heading>Change Password</Heading>
      <Formik
        initialValues={{
          currentPassword: '',
          password: '',
          confirmPassword: '',
          submit: false,
        }}
        validationSchema={Yup.object().shape({
          currentPassword: password,
          password: password,
          confirmPassword: confirmPassword,
        })}
        onSubmit={async (values, { setStatus, setSubmitting, resetForm }) => {
          try {
            await changePassword({ currentPassword: values.currentPassword, newPassword: values.password });
            setUserMessage(userMessages.ChangePassword_Success, 'success');
            resetForm();
          } catch {
            setStatus({ success: false });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            <TextField
              type="password"
              name="currentPassword"
              label="Current Password"
              value={values.currentPassword}
              inputProps={{
                maxLength: 50,
              }}
              error={Boolean(touched.currentPassword && errors.currentPassword)}
              fullWidth
              helperText={touched.currentPassword && errors.currentPassword}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <TextField
              type="password"
              name="password"
              label="New Password"
              value={values.password}
              inputProps={{
                maxLength: 50,
              }}
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <TextField
              type="password"
              name="confirmPassword"
              label="Confirm password"
              value={values.confirmPassword}
              inputProps={{
                maxLength: 50,
              }}
              error={Boolean(touched.confirmPassword && errors.confirmPassword)}
              fullWidth
              helperText={touched.confirmPassword && errors.confirmPassword}
              onBlur={handleBlur}
              onChange={handleChange}
            />
            <Button type="submit" fullWidth variant="contained" color="primary" disabled={isSubmitting} sx={{ mt: 2 }}>
              Change password
            </Button>
          </form>
        )}
      </Formik>
    </Paper>
  );
};

function UserProfile() {
  const email = useSessionStore.use.email();

  return (
    <>
      <PageHeader title="Security" heading="Security" />
      <Paper mt={4} px={6} pb={6}>
        <MainHeading>{email}</MainHeading>
        <ChangePassword />
      </Paper>
    </>
  );
}

export default UserProfile;
